import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef(0);
  const total = movies.length;

  const goTo = useCallback((next: number) => setIndex((next + total) % total), [total]);

  useEffect(() => {
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % total), 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  if (!movies.length) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) goTo(delta < 0 ? index + 1 : index - 1);
  };

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[580px]">
        {movies.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={movie.poster}
              alt={`${movie.title}剧照`}
              title={`${movie.title} - 色色影院焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40"></div>
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl items-center px-4 md:px-6">
          <div key={movies[index].id} className="max-w-xl animate-fade-up">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-50">
                焦点推荐
              </span>
              <span className="rounded-md border border-background-50/30 px-2.5 py-1 text-xs font-medium text-background-100">
                {movies[index].type === "movie" ? "电影" : "电视剧"} · {movies[index].year}
              </span>
              <span className="flex items-center gap-1 text-sm font-bold text-accent-400">
                <i className="ri-star-fill"></i>
                {movies[index].rating.toFixed(1)}
              </span>
            </div>
            <h1 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {movies[index].title}
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-100/85 md:text-base">
              {movies[index].summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${movies[index].id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to="/category"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20"
              >
                浏览全部影视
              </Link>
            </div>
          </div>
        </div>

        <div
          className="absolute inset-0 z-10"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        ></div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {movies.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到${movie.title}`}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}